import DeltaLaunchPopup from "../components/DeltaLaunchPopup";
import React, { useEffect, useRef } from "react";
import CursorSparkle from "../components/CursorSparkle";
import {
  Zap, LineChart, Brain, Activity, Link2, Camera, Shield, MessageSquareText,
  Clock, Gauge, TrendingUp, Share2, ArrowRight, Check, Sparkles, BarChart3, Crown,
  X, ExternalLink, Send, Radar, Target, FileText, Users, Trophy, Newspaper, Coins, LayoutDashboard,
} from "lucide-react";

const SIGNATURE = [
  {
    icon: Radar, badge: "Live", img: "/landing/feat-chart.png", title: "Live Market Analyser",
    desc: "The WS AI Chart reads the live market for you — real-time bias, key support & resistance and clear bull/bear scenarios built from actual spot prices, not a lagging indicator.",
  },
  {
    icon: Target, badge: "Industry First", img: "/landing/feat-wsview.png", title: "WS AI View — Auto Exit & Trailing",
    desc: "For every open position, WS AI View automatically tells you exactly where to book profit and where to trail your stop — recalculating every second as price moves. A first in the industry.",
  },
];

const FEATURES = [
  { icon: Link2, title: "Auto-Sync MT5", desc: "Connect your MT5 broker once — every trade streams in within seconds. No CSVs, no manual entry." },
  { icon: BarChart3, title: "Advanced Analytics", desc: "Equity curve, session & symbol breakdowns, hourly heatmap, streaks and trade-duration vs P&L — all from real trades." },
  { icon: Sparkles, title: "AI Pattern Insights", desc: "Evidence-based patterns with sample size, confidence and a practical recommendation per finding." },
  { icon: FileText, title: "Get My Trading Report", desc: "One tap turns your performance into a polished, shareable PDF trading report — perfect for review or mentors." },
  { icon: Share2, title: "Premium Share Cards", desc: "Turn any closed trade into a branded, shareable result card — download, copy or share in a tap." },
  { icon: Clock, title: "Trade Detail & AI Review", desc: "Every fill, entry/exit, hold time and net realized P&L reconciled — with an AI review of what you did well or wrong." },
  { icon: Brain, title: "AI Behavioral Score", desc: "Real-time scoring across discipline, risk, timing, consistency, strategy and psychology." },
  { icon: MessageSquareText, title: "Ask Your Open Trade", desc: "Chat with any live position — 'hold or close?' — grounded in your real entry, live price and levels. It never invents numbers." },
  { icon: TrendingUp, title: "Live Open Positions", desc: "Every open trade updates in real time with running P&L and a total — powered by 1-second live polling." },
  { icon: Shield, title: "Encrypted & Private", desc: "Credentials are encrypted; no account IDs or passwords ever leave your journal." },
];

const MARQUEE = [
  "Live Market Analyser", "WS AI View: auto exit & trailing", "1-second live open positions",
  "Get My Trading Report (PDF)", "Premium branded share cards", "AI behavioral scoring",
  "Equity curves & drawdown analysis", "Session & symbol breakdowns", "Hourly P&L heatmap",
  "AI chat that knows your trades", "Trade detail + AI review", "Encrypted credentials",
];

const STEPS = [
  { n: "01", title: "Connect Your Broker", desc: "Link your MT5 account in seconds. Trades auto-sync — no manual entry, no hassle." },
  { n: "02", title: "AI Analyzes Everything", desc: "Every trade is scanned for patterns, biases, risk issues and strategy effectiveness." },
  { n: "03", title: "Get Your Edge", desc: "Behavioral scores, session insights and coaching that reference your actual trades." },
];

const STATS = [
  { v: "67%", l: "Win Rate" }, { v: "2.4", l: "Profit Factor" },
  { v: "1:2.1", l: "Avg RR" }, { v: "50+", l: "Metrics / Trade" },
];

const BROKERS = {
  xm: {
    name: "XM Global", logo: "/landing/xm.png",
    accent: "linear-gradient(135deg,#e11d48,#f43f5e)",
    openUrl: "https://www.xm.com/gw.php?gid=259395",
    botUrl: "https://t.me/WS_BBOT",
    note: "Open your XM account through our partner link, then verify with our Telegram bot to auto-connect.",
  },
  elefin: {
    name: "Elefin Trade", logo: "/landing/elefin.png",
    accent: "linear-gradient(135deg,#2563eb,#38bdf8)",
    openUrl: "https://partners.elefin.com/go/wealth",
    botUrl: "https://t.me/WS_BBOT",
    note: "Open your Elefin account through our partner link, then verify with our Telegram bot to auto-connect.",
  },
};

const BrokerConnectModal = ({ broker, onClose }) => {
  if (!broker) return null;
  const b = BROKERS[broker];
  return (
    <div className="ws-broker-modal-overlay" onClick={onClose} data-testid="broker-connect-modal">
      <div className="ws-broker-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ws-broker-modal-close" onClick={onClose} data-testid="broker-modal-close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
        <div className="ws-broker-img mx-auto" style={{ width: 64, height: 64 }}>
          <img src={b.logo} alt={`${b.name} logo`} />
        </div>
        <h3 className="ws-heading text-[20px] font-bold text-center mt-4">Connect {b.name}</h3>
        <p className="text-[13px] text-[color:var(--ws-muted)] text-center mt-2 max-w-sm mx-auto">{b.note}</p>

        <div className="mt-6 space-y-3">
          <a href={b.openUrl} target="_blank" rel="noopener noreferrer" className="ws-broker-step" data-testid="broker-step-open">
            <span className="ws-broker-step-num">1</span>
            <span className="flex-1 text-left">
              <span className="block text-[14px] font-semibold text-white">Open your {b.name} account</span>
              <span className="block text-[12px] text-[color:var(--ws-muted)]">Register via our official partner link</span>
            </span>
            <ExternalLink className="w-4 h-4 text-[color:var(--ws-cyan)]" />
          </a>
          <a href={b.botUrl} target="_blank" rel="noopener noreferrer" className="ws-broker-step" data-testid="broker-step-bot">
            <span className="ws-broker-step-num">2</span>
            <span className="flex-1 text-left">
              <span className="block text-[14px] font-semibold text-white">Verify with our Telegram bot</span>
              <span className="block text-[12px] text-[color:var(--ws-muted)]">Confirm your account to auto-connect</span>
            </span>
            <Send className="w-4 h-4 text-[color:var(--ws-cyan)]" />
          </a>
        </div>

        <button onClick={onClose} className="ws-btn-ghost w-full mt-6 py-2.5 rounded-xl text-[13px] font-semibold" data-testid="broker-modal-later">
          I'll do this later
        </button>
      </div>
    </div>
  );
};

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "Showcase", href: "#showcase" },
  { label: "About", href: "/about" },
  { label: "WS AI Coach", href: "#ai-coach", ai: true },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "/contact" },
];

const FEATURE_SHOWCASE = [
  {
    img: "/landing/command.png", icon: LayoutDashboard, badge: "Command Center",
    title: "Your macro desk, live",
    desc: "Open to a real-time command center — live prices on XAUUSD, BTC, ETH and more, an AI market pulse and your edge on each asset, all in one glance.",
    points: ["Live spot prices, updated every second", "AI market pulse with bias & trade ideas", "Your win rate, profit factor & net at a glance"],
  },
  {
    img: "/landing/journal.png", icon: Link2, badge: "Auto-Sync MT5",
    title: "Every trade, streamed in seconds",
    desc: "Connect your MT5 broker once and every fill flows into a verified journal automatically — no CSVs, no manual entry, no missed trades.",
    points: ["One-time secure broker connect", "Real, verified P&L — never typed by hand", "Encrypted credentials, always private"],
  },
  {
    img: "/landing/analytics.png", icon: BarChart3, badge: "Advanced Analytics",
    title: "See exactly where your edge lives",
    desc: "Equity curve, drawdown, session & symbol breakdowns, an hourly P&L heatmap, streaks and trade-duration analysis — every metric built from your real trades.",
    points: ["Equity curve & drawdown analysis", "Session, symbol & hourly heatmaps", "50+ metrics computed per trade"],
  },
  {
    img: "/landing/feat-chart.png", icon: Radar, badge: "Live Market Analyser",
    title: "AI that reads the live chart for you",
    desc: "The WS AI Chart turns raw spot prices into a clear real-time bias, key support & resistance and honest bull/bear scenarios — never a lagging indicator.",
    points: ["Real-time bias from actual spot prices", "Auto-marked support & resistance", "Clear bull vs bear scenarios"],
  },
  {
    img: "/landing/feat-wsview.png", icon: Target, badge: "Industry First",
    title: "WS AI View — auto exit & trailing",
    desc: "For every open position, WS AI View tells you exactly where to book profit and where to trail your stop — recalculating every single second as price moves.",
    points: ["Auto booking target on every trade", "Live trailing-stop zone", "Recalculated each second"],
  },
  {
    img: "/landing/coach.png", icon: Brain, badge: "WS AI Coach",
    title: "It coaches every trade, not just logs it",
    desc: "Your always-on mentor scores discipline, risk, timing and psychology, reviews every closed trade and answers “hold or close?” — all grounded in your real numbers.",
    points: ["Behavioral score across 6 dimensions", "AI review of every closed trade", "Ask your live trade — grounded, never invented"],
  },
  {
    img: "/landing/news.png", icon: Newspaper, badge: "WS News Pro",
    title: "Market-moving news, decoded",
    desc: "A real-time newsroom that filters the noise — high-impact headlines, economic events and AI context on what each one means for your instruments.",
    points: ["Real-time high-impact headlines", "Economic calendar & event flags", "AI take on market impact"],
  },
  {
    img: "/landing/community.png", icon: Users, badge: "VIP Community",
    title: "Trade alongside the whole family",
    desc: "A Telegram-style broadcast community where the Wealth Secret desk shares live calls, market notes, charts and polls — with push notifications so you never miss one.",
    points: ["Live calls & market notes from the desk", "Charts, media & polls", "Push notifications on every drop"],
  },
  {
    img: "/landing/growth.png", icon: Coins, badge: "Growth Zone",
    title: "Get rewarded for showing up",
    desc: "Earn WS Coins for daily sign-ins, journaling and inviting friends — climb the ranks from Bronze to Diamond and redeem for premium perks.",
    points: ["Daily sign-in & journaling rewards", "Refer & earn with your own link", "Bronze → Diamond rank progression"],
  },
  {
    img: "/landing/leaderboard.png", icon: Trophy, badge: "WS Leaderboard",
    title: "Prove your edge on the board",
    desc: "A global leaderboard ranking real connected traders by win rate, return, net P&L and volume — with a podium for the top performers.",
    points: ["Ranked by real MT5 results", "Win rate, return % & net P&L", "Top-3 podium & your live position"],
  },
];

const AI_COACH = [
  { icon: Radar, badge: "Live", title: "Live Market Read", desc: "Real-time bias, key support & resistance and bull/bear scenarios built from actual spot prices on any chart." },
  { icon: Target, badge: "Industry First", title: "Auto Exit & Trailing", desc: "Tells you exactly where to book profit and where to trail your stop on every open trade — recalculating each second." },
  { icon: MessageSquareText, badge: "Co-pilot", title: "Ask Your Trade", desc: "Chat “hold or close?” on any live position — answers grounded in your real entry, price and levels." },
  { icon: Sparkles, badge: "Industry First", title: "Evidence Insights", desc: "Pattern insights with real sample size, confidence and a practical recommendation — never vague advice." },
  { icon: Brain, title: "Behavioral Coaching", desc: "Scores your discipline, risk, timing, consistency and psychology — then coaches the exact leaks." },
  { icon: Clock, title: "AI Trade Reviews", desc: "Every closed trade reviewed: entry/exit quality, R:R, hold time and 2–3 concrete lessons." },
  { icon: Shield, badge: "Grounded", title: "Never Invents Numbers", desc: "Every answer is grounded in your real broker data — no hallucinated prices or fake levels, ever." },
  { icon: FileText, title: "AI Trading Report", desc: "Turns your entire history into a coach-style PDF report you can actually act on." },
];

const Nav = ({ onGetStarted }) => (
  <nav className="ws-nav-wrap" data-testid="landing-nav">
    <div className="ws-nav-pill">
      <a href="#top" className="ws-nav-brand" data-testid="landing-nav-brand">
        <span className="ws-logo-3d w-9 h-9"><img src="/wsj-logo.webp" alt="Wealth Secret Journal" /></span>
        <span className="leading-none">
          <span className="block text-white font-extrabold text-[15px] tracking-tight">Wealth Secret</span>
          <span className="block text-[9px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Journal</span>
        </span>
      </a>

      <div className="ws-nav-links">
        {NAV_LINKS.map((l) => (
          <a key={l.label} href={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`ws-nav-link ${l.ai ? "ws-nav-link-ai" : ""}`}>
            {l.ai && <Sparkles className="w-3.5 h-3.5 ws-nav-ai-spark" />}
            {l.label}
          </a>
        ))}
      </div>

      <div className="ws-nav-actions">
        <button onClick={onGetStarted} className="ws-nav-signin" data-testid="landing-signin">
          Sign in <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button onClick={onGetStarted} className="ws-btn-3d ws-nav-cta" data-testid="landing-getstarted-nav">
          <Sparkles className="w-4 h-4" /> Start Free
        </button>
      </div>
    </div>
  </nav>
);

const LandingPage = ({ onGetStarted }) => {
  const [activeBroker, setActiveBroker] = React.useState(null);
  const progressRef = useRef(null);
  const heroRef = useRef(null);
  const mockRef = useRef(null);

  useEffect(() => {
    const els = document.querySelectorAll(".ws-reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("ws-reveal-in"); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Scroll progress bar
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const h = document.documentElement;
        const max = h.scrollHeight - h.clientHeight;
        const p = max > 0 ? (h.scrollTop || window.scrollY) / max : 0;
        if (progressRef.current) progressRef.current.style.transform = `scaleX(${p})`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Hero cursor spotlight + subtle parallax tilt on the dashboard mockup
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const onMove = (e) => {
      const r = hero.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      hero.style.setProperty("--mx", `${(x * 100).toFixed(2)}%`);
      hero.style.setProperty("--my", `${(y * 100).toFixed(2)}%`);
      if (mockRef.current) {
        mockRef.current.style.setProperty("--px", `${((x - 0.5) * 12).toFixed(2)}deg`);
        mockRef.current.style.setProperty("--py", `${((0.5 - y) * 8).toFixed(2)}deg`);
      }
    };
    hero.addEventListener("pointermove", onMove);
    return () => hero.removeEventListener("pointermove", onMove);
  }, []);

  return (
    <div id="top" className="min-h-screen text-white" data-testid="landing-page">
      <DeltaLaunchPopup onConnect={onGetStarted} ctaLabel="Sign in & connect Delta" />
      <CursorSparkle />
      <div className="ws-scroll-progress" aria-hidden><span ref={progressRef} /></div>
      <Nav onGetStarted={onGetStarted} />

      {/* Hero */}
      <header ref={heroRef} className="ws-hero relative max-w-6xl mx-auto px-6 pt-36 pb-20 text-center">
        <div className="ws-hero-fx" aria-hidden>
          <div className="ws-hero-grid" />
          <div className="ws-hero-spot" />
          <div className="ws-hero-beam" />
          <div className="ws-particles">
            {Array.from({ length: 16 }).map((_, i) => <span key={i} className="ws-particle" style={{ "--i": i }} />)}
          </div>
        </div>
        <div className="relative z-[1]">
        <div className="ws-logo-3d ws-hero-logo" data-testid="landing-hero-logo">
          <img src="/wsj-logo.webp" alt="Wealth Secret Journal logo" />
        </div>
        <span className="ws-fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-medium mb-7"
          style={{ background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.3)", color: "#38bdf8" }}>
          <span className="ws-live-dot" /> AI-Powered Trading Journal
        </span>
        <h1 className="ws-fade-up ws-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] ws-hero-title" style={{ animationDelay: "80ms" }}>
          <span className="ws-brand-name">Wealth Secre<span className="ws-crown-word">t<span className="ws-crown" aria-hidden="true"><Crown className="w-full h-full" strokeWidth={2} /></span></span></span>, <span className="ws-title-gradient">Decoded.</span><br />
          Your Trading Edge, <span className="ws-title-gradient">Unlocked.</span>
        </h1>
        <p className="ws-fade-up mx-auto max-w-2xl mt-6 text-base sm:text-lg text-[color:var(--ws-muted)]" style={{ animationDelay: "140ms" }}>
          The trading journal that auto-syncs your MT5 account, finds patterns in your behavior,
          and turns raw trade data into a measurable edge.
        </p>
        <div className="ws-fade-up flex items-center justify-center gap-3 mt-9" style={{ animationDelay: "200ms" }}>
          <button onClick={onGetStarted} className="ws-btn-primary px-6 py-3 rounded-xl text-[14px] font-semibold inline-flex items-center gap-2" data-testid="landing-getstarted-hero">
            Get Started Free <ArrowRight className="w-4 h-4" />
          </button>
          <a href="#features" className="ws-btn-3d px-6 py-3 rounded-xl text-[14px] font-semibold inline-flex items-center gap-2" data-testid="landing-explore-btn">
            Explore Wealth Secret <ArrowRight className="w-4 h-4 ws-btn-3d-arrow" />
          </a>
        </div>

        {/* Stat strip */}
        <div className="ws-fade-up grid grid-cols-2 sm:grid-cols-4 gap-3 mt-16 max-w-3xl mx-auto" style={{ animationDelay: "260ms" }}>
          {STATS.map((s, i) => (
            <div key={s.l} className="ws-panel ws-stat-pop p-5" style={{ animationDelay: `${300 + i * 90}ms` }}>
              <div className="ws-heading text-[26px] font-bold ws-title-gradient">{s.v}</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--ws-muted)] mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        {/* 3D dashboard preview — real app screenshots */}
        <div id="showcase" className="ws-fade-up mt-20 ws-tilt-wrap ws-parallax-wrap" style={{ animationDelay: "320ms" }} data-testid="hero-mockup">
          <div ref={mockRef} className="ws-tilt ws-shot-frame ws-parallax mx-auto max-w-5xl text-left">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--ws-border)]" style={{ background: "rgba(10,17,33,0.9)" }}>
              <span className="w-3 h-3 rounded-full" style={{ background: "#f87171" }} />
              <span className="w-3 h-3 rounded-full" style={{ background: "#fbbf24" }} />
              <span className="w-3 h-3 rounded-full" style={{ background: "#34d399" }} />
              <span className="ml-3 text-[11px] text-[color:var(--ws-muted)]">wealthsecret.app/analytics</span>
              <span className="ml-auto ws-live-dot" /><span className="text-[10px] text-[color:var(--ws-muted)]">live · real MT5 data</span>
            </div>
            <img
              src="/landing/analytics.png"
              alt="Wealth Secret Analytics dashboard with real MT5 data — profit factor, session performance, hourly heatmap and AI pattern insights"
              className="block w-full h-auto"
              loading="eager"
              data-testid="hero-shot-analytics"
            />
          </div>

          {/* Floating AI Journal card for depth */}
          <div className="ws-shot-float ws-shot-frame" data-testid="hero-shot-journal-wrap">
            <img
              src="/landing/journal.png"
              alt="Wealth Secret AI Journal — live MT5 sync, win rate, profit factor and verified performance stats"
              className="block w-full h-auto"
              loading="lazy"
              data-testid="hero-shot-journal"
            />
          </div>
        </div>
        </div>
      </header>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-16 ws-reveal">
        <div className="text-center mb-12">
          <p className="text-[12px] uppercase tracking-[0.2em] text-[color:var(--ws-cyan)] font-semibold mb-2">Platform</p>
          <h2 className="ws-heading text-3xl sm:text-4xl font-bold">Built for serious traders</h2>
          <p className="text-[color:var(--ws-muted)] mt-3 max-w-xl mx-auto text-sm sm:text-base">Everything you need to journal, analyze and improve — powered by AI that actually understands your trading.</p>
        </div>

        {/* Signature / industry-first features with real screenshots */}
        <div className="grid md:grid-cols-2 gap-5 mb-5">
          {SIGNATURE.map((f, i) => (
            <div key={f.title} className="ws-sig-card ws-sig-showcase ws-fade-up" style={{ animationDelay: `${i * 90}ms` }} data-testid={`signature-${i}`}>
              {f.badge && <span className="ws-sig-badge">{f.badge}</span>}
              <div className="ws-sig-shot">
                <img src={f.img} alt={f.title} loading="lazy" data-testid={`signature-shot-${i}`} />
              </div>
              <div className="ws-sig-body">
                <div className="flex items-center gap-3 mb-2">
                  <span className="ws-sig-icon"><f.icon className="w-5 h-5" /></span>
                  <h3 className="ws-heading text-[17px] font-bold">{f.title}</h3>
                </div>
                <p className="text-[13px] text-[color:var(--ws-muted)] leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="ws-panel ws-panel-hover ws-shine p-6 ws-fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`feature-${i}`}>
              <div className="ws-icon-chip ws-icon-pop w-10 h-10 mb-4"><f.icon className="w-5 h-5" /></div>
              <h3 className="ws-heading text-[16px] font-semibold mb-1.5">{f.title}</h3>
              <p className="text-[13px] text-[color:var(--ws-muted)] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Every feature — screenshot showcase */}
      <section id="showcase-all" className="ws-showcase-all max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-14 ws-reveal">
          <p className="text-[12px] uppercase tracking-[0.2em] text-[color:var(--ws-cyan)] font-semibold mb-2">Every feature, in action</p>
          <h2 className="ws-heading text-3xl sm:text-4xl font-bold">See the whole platform — nothing hidden</h2>
          <p className="text-[color:var(--ws-muted)] mt-3 max-w-xl mx-auto text-sm sm:text-base">Real screens from the actual app. Every tool below is live and 100% free.</p>
        </div>

        <div className="ws-showcase-rows">
          {FEATURE_SHOWCASE.map((f, i) => (
            <div key={f.title} className={`ws-showcase-row ${i % 2 ? "is-rev" : ""} ws-reveal ${i % 2 ? "ws-reveal-right" : "ws-reveal-left"}`} data-testid={`showcase-row-${i}`}>
              <div className="ws-showcase-media ws-shine">
                <div className="ws-showcase-bar">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#f87171" }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#fbbf24" }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#34d399" }} />
                  <span className="ml-2 text-[10px] text-[color:var(--ws-muted)] truncate">wealthsecret.app</span>
                  <span className="ml-auto ws-live-dot" /><span className="text-[10px] text-[color:var(--ws-muted)]">live</span>
                </div>
                <img src={f.img} alt={f.title} loading="lazy" data-testid={`showcase-img-${i}`} />
                <div className="ws-showcase-media-glow" aria-hidden />
              </div>
              <div className="ws-showcase-copy">
                <span className="ws-showcase-badge"><f.icon className="w-3.5 h-3.5" /> {f.badge}</span>
                <h3 className="ws-heading text-2xl sm:text-3xl font-bold mt-4 mb-3">{f.title}</h3>
                <p className="text-[14px] text-[color:var(--ws-muted)] leading-relaxed">{f.desc}</p>
                <ul className="ws-showcase-points">
                  {f.points.map((p) => (
                    <li key={p}><span className="ws-showcase-check"><Check className="w-3 h-3" /></span> {p}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WS AI Coach — the AI mentor */}
      <section id="ai-coach" className="ws-coach-section ws-reveal">
        <div className="ws-coach-aurora" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center mb-10">
            <span className="ws-coach-eyebrow" data-testid="ai-coach-eyebrow">
              <Sparkles className="w-3.5 h-3.5" /> WS AI Coach
            </span>
            <h2 className="ws-heading text-3xl sm:text-5xl font-extrabold mt-4 leading-[1.05]">
              Your always-on <span className="ws-coach-grad">AI trading mentor</span>
            </h2>
            <p className="text-[color:var(--ws-muted)] mt-4 max-w-2xl mx-auto text-sm sm:text-base">
              WS AI Coach watches your charts and every open trade in real time — telling you where to book,
              where to trail, what your patterns reveal and how to fix them. All grounded in your real MT5 data.
            </p>
          </div>

          {/* Coach hero: live example screenshot + capability chips */}
          <div className="ws-coach-hero mb-8">
            <div className="ws-coach-shot">
              <div className="ws-coach-shot-bar">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#f87171" }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#fbbf24" }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#34d399" }} />
                <span className="ml-2 text-[10px] text-[color:var(--ws-muted)]">WS AI View · live position coaching</span>
                <span className="ml-auto ws-live-dot" />
              </div>
              <img src="/landing/feat-wsview.png" alt="WS AI Coach analysing a live position — where to book and trail" loading="lazy" data-testid="ai-coach-shot" />
            </div>
            <div className="ws-coach-hero-body">
              <h3 className="ws-heading text-[20px] font-bold mb-3">It coaches every trade — not just logs it</h3>
              <ul className="space-y-2.5">
                {[
                  "Reads live bias, support & resistance from real spot prices",
                  "Auto-marks your booking target and trailing-stop zone",
                  "Answers “hold or close?” with your actual numbers",
                  "Flags behavioral leaks and your best sessions & symbols",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-[13.5px] text-white/90">
                    <span className="ws-coach-check"><Check className="w-3 h-3" /></span> {t}
                  </li>
                ))}
              </ul>
              <button onClick={onGetStarted} className="ws-btn-3d ws-coach-cta mt-6" data-testid="ai-coach-cta">
                <Sparkles className="w-4 h-4" /> Meet your AI Coach
              </button>
            </div>
          </div>

          {/* AI capability grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {AI_COACH.map((f, i) => (
              <div key={f.title} className="ws-coach-card ws-shine ws-fade-up" style={{ animationDelay: `${i * 45}ms` }} data-testid={`ai-coach-${i}`}>
                {f.badge && <span className={`ws-coach-badge ${f.badge === "Industry First" ? "is-first" : ""}`}>{f.badge}</span>}
                <div className="ws-coach-icon mb-3"><f.icon className="w-4 h-4" /></div>
                <h4 className="ws-heading text-[14.5px] font-semibold mb-1">{f.title}</h4>
                <p className="text-[12.5px] text-[color:var(--ws-muted)] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee of everything */}
      <section className="ws-marquee-mask py-10 overflow-hidden border-y border-[color:var(--ws-border)]" style={{ background: "rgba(10,17,33,0.4)" }}>
        <div className="ws-marquee flex gap-3 w-max">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="ws-price-cell px-4 py-2 text-[13px] whitespace-nowrap inline-flex items-center gap-2">
              <Check className="w-3.5 h-3.5 ws-up" /> {m}
            </span>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-20 ws-reveal">
        <div className="text-center mb-12">
          <p className="text-[12px] uppercase tracking-[0.2em] text-[color:var(--ws-cyan)] font-semibold mb-2">How it works</p>
          <h2 className="ws-heading text-3xl sm:text-4xl font-bold">Three steps to better trading</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.n} className="ws-panel p-7 ws-fade-up relative" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="ws-heading text-[44px] font-extrabold ws-title-gradient leading-none opacity-70">{s.n}</div>
              <h3 className="ws-heading text-[18px] font-semibold mt-3 mb-2">{s.title}</h3>
              <p className="text-[13px] text-[color:var(--ws-muted)] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Brokers */}
      <section className="max-w-5xl mx-auto px-6 py-16 ws-reveal" data-testid="brokers-section">
        <div className="text-center mb-10">
          <p className="text-[12px] uppercase tracking-[0.2em] text-[color:var(--ws-cyan)] font-semibold mb-2">Brokers</p>
          <h2 className="ws-heading text-3xl sm:text-4xl font-bold">Connect your broker in seconds</h2>
          <p className="text-[color:var(--ws-muted)] mt-3 text-sm sm:text-base">Auto-sync your trades from XM, Elefin and now Delta Exchange India — more exchanges landing soon.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* XM — featured */}
          <button type="button" onClick={() => setActiveBroker("xm")} className="ws-panel p-6 text-center ws-broker-featured ws-broker-clickable" data-testid="broker-xm">
            <span className="ws-broker-badge">Featured</span>
            <div className="ws-broker-img" data-testid="broker-xm-logo">
              <img src="/landing/xm.png" alt="XM Global broker logo" loading="lazy" />
            </div>
            <h3 className="ws-heading text-[16px] font-semibold mt-3">XM Global</h3>
            <p className="text-[12px] ws-up mt-1 font-semibold inline-flex items-center gap-1 justify-center">Connect now <ArrowRight className="w-3 h-3" /></p>
          </button>
          {/* Elefin — featured/highlighted */}
          <button type="button" onClick={() => setActiveBroker("elefin")} className="ws-panel p-6 text-center ws-broker-featured ws-broker-clickable" data-testid="broker-elefin">
            <span className="ws-broker-badge">Featured</span>
            <div className="ws-broker-img" data-testid="broker-elefin-logo">
              <img src="/landing/elefin.png" alt="Elefin Trade broker logo" loading="lazy" />
            </div>
            <h3 className="ws-heading text-[16px] font-semibold mt-3">Elefin Trade</h3>
            <p className="text-[12px] ws-up mt-1 font-semibold inline-flex items-center gap-1 justify-center">Connect now <ArrowRight className="w-3 h-3" /></p>
          </button>
          {/* Shark — coming soon */}
          <div className="ws-panel p-6 text-center opacity-90" data-testid="broker-shark">
            <div className="ws-broker-img" data-testid="broker-shark-logo">
              <img src="/landing/shark.png" alt="Shark Exchange broker logo" loading="lazy" />
            </div>
            <h3 className="ws-heading text-[16px] font-semibold mt-3">Shark Exchange</h3>
            <p className="text-[12px] text-[color:var(--ws-muted)] mt-1">Coming soon</p>
          </div>
          {/* Delta Exchange India — LIVE (API key connect) */}
          <button type="button" onClick={onGetStarted} className="ws-panel p-6 text-center ws-broker-featured ws-broker-clickable ws-broker-new" data-testid="broker-delta">
            <span className="ws-broker-badge ws-broker-badge-new">New · Live</span>
            <div className="ws-broker-img" data-testid="broker-delta-logo">
              <img src="/landing/delta.png" alt="Delta Exchange India logo" loading="lazy" />
            </div>
            <h3 className="ws-heading text-[16px] font-semibold mt-3">Delta Exchange India</h3>
            <p className="text-[12px] ws-up mt-1 font-semibold inline-flex items-center gap-1 justify-center">Connect with API key <ArrowRight className="w-3 h-3" /></p>
          </button>
        </div>
        <div id="pricing" className="ws-panel p-6 mt-6 text-center" style={{ background: "radial-gradient(120% 100% at 50% 0%, rgba(52,211,153,0.12), rgba(0,0,0,0) 60%)" }}>
          <div className="inline-flex items-center gap-2 text-[color:var(--ws-green)] font-semibold text-[15px]"><Check className="w-5 h-5" /> 100% Free — every feature, no paywalls</div>
          <p className="text-[13px] text-[color:var(--ws-muted)] mt-1">Unlimited trades, AI insights, analytics and share cards. No credit card, ever.</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center ws-reveal">
        <div className="ws-panel p-12" style={{ background: "radial-gradient(120% 100% at 50% 0%, rgba(56,189,248,0.12), rgba(0,0,0,0) 60%)" }}>
          <BarChart3 className="w-10 h-10 mx-auto mb-4 text-[color:var(--ws-cyan)]" />
          <h2 className="ws-heading text-3xl sm:text-4xl font-bold">Ready to find your edge?</h2>
          <p className="text-[color:var(--ws-muted)] mt-3 max-w-lg mx-auto text-sm sm:text-base">Stop guessing. Start journaling with AI that turns your trade data into actionable insights.</p>
          <button onClick={onGetStarted} className="ws-btn-primary px-7 py-3.5 rounded-xl text-[15px] font-semibold inline-flex items-center gap-2 mt-8" data-testid="landing-getstarted-final">
            Get Started Free <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-[color:var(--ws-border)] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-[color:var(--ws-muted)] text-[13px]">
            <Zap className="w-4 h-4 text-[color:var(--ws-cyan)]" /> Wealth Secret Journal
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px]">
            <a href="/about" className="text-[color:var(--ws-muted)] hover:text-white transition-colors" data-testid="footer-about">About</a>
            <a href="/contact" className="text-[color:var(--ws-muted)] hover:text-white transition-colors" data-testid="footer-contact">Contact</a>
            <a href="mailto:wealthsecret23@gmail.com" className="text-[color:var(--ws-muted)] hover:text-white transition-colors" data-testid="footer-email">wealthsecret23@gmail.com</a>
            <a href="/disclaimer" className="text-[color:var(--ws-muted)] hover:text-white transition-colors" data-testid="footer-disclaimer">Disclaimer</a>
            <a href="/privacy" className="text-[color:var(--ws-muted)] hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms" className="text-[color:var(--ws-muted)] hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</a>
          </nav>
          <p className="text-[12px] text-[color:var(--ws-muted)]">© 2026 Wealth Secret. Real MT5 data.</p>
        </div>
        <p className="max-w-6xl mx-auto px-6 mt-5 text-[11px] leading-relaxed text-[color:var(--ws-muted)]/70">
          Risk warning: Trading leveraged products carries a high level of risk and may not be suitable for all investors. The content on Wealth Secret is for educational purposes only and is not financial advice. Past performance is not indicative of future results.
        </p>
      </footer>

      <BrokerConnectModal broker={activeBroker} onClose={() => setActiveBroker(null)} />
    </div>
  );
};

export default LandingPage;
